/* eslint-disable react-refresh/only-export-components */
// This module intentionally exports the pure `productDetailSignature` helper
// alongside the tracker component so the helper is unit-testable (same rationale
// as the file-level disable in `root.tsx`); Fast Refresh isn't relevant to a
// leaf analytics side-effect component.
import { useEffect, useRef } from "react";

import { pushEcommerce } from "~/hec/analytics/data-layer.client";
import {
  type Ga4ProductDetailItem,
  buildGa4ProductDetailEcommerce,
} from "~/hec/analytics/ga4-product-detail";
import { trackAbTastyEvent } from "~/lib/track-abtasty-event.client";

// SFCC-11804 — a stable signature so the tracker re-fires only when the viewed
// product(s) actually change, not on every render (`usePdpGa4Items` returns a
// fresh array each render). Keyed by product id + selected variant.
export const productDetailSignature = (items: Ga4ProductDetailItem[]): string =>
  items.map(i => `${i.item_id}:${i.item_variant ?? ""}`).join(",");

/**
 * Pushes GA4 `GA4_productDetail` on SPA navigation to a PDP (SFCC-11804).
 *
 * The first-paint / hard-load push is handled by `DataLayerClientPushes` — a
 * run-once (`[]`-deps) effect in the root shell that never re-runs on client
 * navigation because the shell never re-mounts. So a PDP reached via a `<Link>`
 * (PLP / search / PDP→PDP — the common path) otherwise emits no product-view
 * event, and GA4 records `view_item` only on full page loads.
 *
 * Mirrors `PageTypeTracker` / `ClickSourceTracker`: seeded with the first-paint
 * signature so hydration doesn't double-push what `DataLayerClientPushes`
 * already sent, then re-pushes when the viewed product changes. Leaving a PDP
 * (no items) resets the seed so returning to the same product re-fires; non-PDP
 * routes push nothing.
 *
 * `ga4Items` is the root's `usePdpGa4Items()` value — the same source
 * `DataLayerClientPushes` receives — so the seed matches the first-paint push.
 */
export function ProductDetailTracker({
  ga4Items,
}: {
  ga4Items: Ga4ProductDetailItem[];
}) {
  const signature = productDetailSignature(ga4Items);
  // Seed = first-paint signature; the first effect tick early-returns so
  // hydration doesn't double-push what DataLayerClientPushes already sent.
  const lastPushed = useRef<string>(signature);
  useEffect(() => {
    // Left the PDP (or a non-PDP route): reset so a later return to the same
    // product re-fires, and never push an empty product-view event.
    if (!ga4Items.length) {
      lastPushed.current = "";
      return;
    }
    if (lastPushed.current === signature) return;
    lastPushed.current = signature;
    // SFCC-11890: flush `{ecommerce:null}` first so a prior page's ecommerce
    // items don't bleed into this product-view event on SPA nav.
    // Event-level value + currency (SFCC-11889 C1), matching the hard-load
    // push in DataLayerClientPushes.
    pushEcommerce({
      event: "GA4_productDetail",
      ecommerce: buildGa4ProductDetailEcommerce(ga4Items),
    });
    // AB Tasty Universal Collect EVENT hit (SFCC-12079) — SPA-navigation PDP
    // view. The hard-load/first-paint case is covered by `DataLayerClientPushes`.
    trackAbTastyEvent({ ec: "User Engagement", ea: "view_item" });
    // `signature` is the true trigger; `ga4Items` is content-equivalent to it
    // (the signature is derived from the items), so keying on it alone avoids a
    // per-render effect run without going stale.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);
  return null;
}
